import { useMemo } from 'react';
import { get } from 'lodash';
import { formatNumber } from '../util/data';
import { getPackage, getLodgingPricesOptions, getPackageImages } from '../util/listing';
import { getStartingPrice, getPackageLodgingPrices, getFlexiblePricingData } from '../util/package';
import { formatMoney } from '../util/currency';
import { types as sdkTypes } from '../util/sdkLoader';

const { Money } = sdkTypes;

/**
 * Custom hook to extract and format package data from listing
 * @param {Object} params
 * @param {Object} params.listing - The listing object
 * @param {string} params.packageId - The package ID
 * @param {Object} params.intl - Intl object for formatting (required)
 * @returns {Object} - Formatted package data
 */
const usePackageData = ({ listing, packageId, intl }) => {
  const packageData = useMemo(() => {
    // Get the package from listing
    const currentPackage = getPackage(listing, packageId);

    if (!currentPackage || !listing) {
      return null;
    }

    // Extract listing data
    const publicData = listing?.attributes?.publicData || {};
    const listingTitle = listing?.attributes?.title || '';

    // Extract package data
    const activity = currentPackage?.activity;
    const packageTitle = currentPackage?.title || '';
    const packageGMU = currentPackage?.species_unit;

    // Extract and format listing data
    const nearestTown = get(publicData, 'nearestTown.selectedPlace.address', null);
    const acreage = get(publicData, 'location.acreage', 0);
    const formattedAcres = acreage ? formatNumber(acreage) : null;

    // Calculate pricing
    const lodgingPricesOptions = getLodgingPricesOptions(listing, intl);
    const { optionalLodging, includedLodging } = getPackageLodgingPrices(
      currentPackage,
      lodgingPricesOptions
    );
    const packageMinParty = currentPackage?.guests?.min || 1;
    const flexiblePricingData = getFlexiblePricingData(
      currentPackage?.flexiblePricing,
      packageMinParty
    );

    const startingPriceMoney = getStartingPrice({
      ...currentPackage,
      optionalLodging,
      includedLodging,
      flexiblePricing: flexiblePricingData,
    });

    // Format price
    const formattedPrice = startingPriceMoney
      ? formatMoney(intl, new Money(startingPriceMoney.amount, startingPriceMoney.currency), true)
      : null;

    // Build subtitle as a string
    const subtitle = intl.formatMessage(
      {
        id: 'PackageCard.subtitle',
        defaultMessage: '{acres} Acres at {listingTitle}{locationText}',
      },
      {
        acres: formattedAcres,
        listingTitle,
        locationText: nearestTown ? ` in ${nearestTown}` : '',
      }
    );

    // Get package images
    const packagesImages = getPackageImages(listing, packageId);
    const headerImages = packagesImages?.length ? packagesImages : listing.images;
    const images = (headerImages || [])
      .map(image => {
        const variants = image?.attributes?.variants;
        return variants?.['scaled-large']?.url || variants?.default?.url;
      })
      .filter(Boolean);

    return {
      title: packageTitle,
      activity,
      gmu: packageGMU,
      listingTitle,
      nearestTown,
      acreage: formattedAcres,
      startingPrice: formattedPrice,
      subtitle,
      images,
    };
  }, [listing, packageId, intl]);

  return packageData;
};

export default usePackageData;
